<template>
  <div class="universal-select" :class="{'readonly-mode' : readonly}">
    <span v-if="title" class="universal-select__title">{{ title }}</span>
    <div v-if="showCopyIcon" class="universal-select__copy-icon">
      <a-icon
        slot="addonBefore"
        type="copy"
        class="font-size-18"
        :class="hasValue ? 'text-primary cursor-pointer' : 'text-default cursor-default'"
        @click="copy"
      />
    </div>
    <a-select
      ref="select"
      :allow-clear="allowClear"
      :value="value"
      size="large"
      :class="{
        'transparent-select' : readonly,
        'pl-select' : showCopyIcon,
      }"
      :showArrow="!readonly"
      :disabled="disabled && !searchCallback"
      :loading="loading"
      :show-search="showSearch"
      :filter-option="false"
      :get-popup-container="getPopupContainer"
      @search="handleSearch"
      @change="handleChange"
    >
      <template v-if="placeholder" #placeholder>
        <span style="color: #a1a1c2;">{{ placeholder }}</span>
      </template>
      <a-select-option
        v-for="(option, index) in filteredOptions"
        :key="option.value"
        class="universal-select__option"
        :class="{'universal-select__option-highlight' : index === 0 && value !== option.value && highlightFirst}"
      >
        <div class="universal-select__option__content">
          <div v-if="option?.platform" class="universal-select__option__content-icon">
            <a-icon v-if="option.platform.toLowerCase() === 'android'" type="android" class="text-success" />
            <a-icon v-if="option.platform.toLowerCase() === 'ios'" type="apple" class="text-primary" />
            <a-icon v-if="option.platform.toLowerCase() === 'web'" type="global" class="text-default" />
          </div>
          <span>{{ option.name }}</span>
          <div class="universal-select__option__content__suffix">
            <slot name="suffix" :option="option" />
          </div>
        </div>
      </a-select-option>
    </a-select>
  </div>
</template>

<script>
export default {
  name: 'universalSelect',
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    highlightFirst: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [Number, String],
      default: undefined,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    searchCallback: {
      type: Boolean,
      default: false,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
    showCopyIcon: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    displayOptionsAtBottom: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    filteredOptions: [],
    searchTimeout: null,
  }),
  watch: {
    options: {
      handler(val) {
        this.setAvailableOptions(val)
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    hasValue() {
      return !!this.value
    },
  },
  methods: {
    setAvailableOptions(options) {
      this.filteredOptions = JSON.parse(JSON.stringify(options))
    },
    getPopupContainer(trigger) {
      if (this.displayOptionsAtBottom) {
        return document.body
      }
      return trigger.parentElement
    },
    copy() {
      const copyOption = this.options.find(opt => opt.value === this.value)
      if (!copyOption) {
        return
      }
      this.$emit('copy', copyOption.name)
    },
    handleSearch(searchValue) {
      if (this.searchCallback) {
        this.handleSearchCallback(searchValue)
        return
      }
      this.filteredOptions = this.options.filter(option => {
        if (option.name) {
          return option.name.toLowerCase().includes(searchValue.toLowerCase())
        }
      })
    },
    handleSearchCallback(searchValue) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.$emit('searchCallback', searchValue)
        clearTimeout(this.searchTimeout)
      }, 1000)
    },
    handleChange(value) {
      this.$emit('change', value)
      this.setAvailableOptions(this.options)
    },
  },
}
</script>

<style lang="scss">
.universal-select {
  display: flex;
  flex-direction: column;
  &__title {
    font-weight: bold;
  }
  &__copy-icon {
    position: absolute;
    z-index: 1;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f0f2f4;
    border: 1px solid #e4e9f0;
  }
  &__option {
    &-highlight {
      font-style: italic !important;
    }
    &__content {
      display: flex;
      gap: 8px;
      &__suffix {
        display: flex;
        flex: 1 1 auto;
        justify-content: flex-end;
        align-items: center;
        margin-right: 10px;
      }
    }
    &__content-icon {
      display: inline;
    }
  }
  .pl-select {
    padding-left: 40px;
  }
  .ant-select-selection-selected-value {
    width: 100%;
  }
}
.readonly-mode {
  .universal-select__copy-icon {
    width: fit-content;
    height: fit-content;
    background-color: unset;
    border: none;
  }
  .pl-select {
    padding-left: 30px;
  }
}
</style>
